/* eslint-disable @atlaskit/design-system/ensure-design-token-usage, @atlaskit/design-system/ensure-design-token-usage/preview, react/jsx-no-bind */
import React, { useState } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useRouter } from 'react-resource-router';
import { useConversation } from 'unified-help-center-alpha/src/store/converse';
import { SEARCH_INPUT_FONT_SIZE } from 'view/common/styled';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import * as transitions from 'view/styles/transitions';
import { searchTextbox } from 'view/styles/z-index';
import Button from '@atlaskit/button';
import Icon from '@atlaskit/icon';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { UnstyledButton } from '@atlassian/help-center-common-component/unstyled-button';
import { ConversationExperience } from '../../conversation';
import type { unifiedHelpSearchTextboxQuery } from './__generated__/unifiedHelpSearchTextboxQuery.graphql';
import messages from './messages';

export const UnifiedHelpSearchTextBox = () => {
    const [isChoiceOpen, setIsChoiceOpen] = useState(false);
    const [isUHExperienceSelected, setIsUHExperienceSelected] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [{ location }, { push }] = useRouter();
    const { formatMessage } = useIntl();
    const [, { startNewConversationWithAiMate }] = useConversation();

    const data = useLazyLoadQuery<unifiedHelpSearchTextboxQuery>(
        graphql`
            query unifiedHelpSearchTextboxQuery {
                me @required(action: THROW) {
                    user @required(action: THROW) {
                        accountId
                    }
                }
            }
        `,
        {}
    );

    const openChoicesOnChange = (term: string) => {
        setSearchQuery(term);
        term !== '' ? setIsChoiceOpen(true) : setIsChoiceOpen(false);
    };

    const redirectToAllResults = () => {
        push(`${location.pathname}?q=${searchQuery}`) as unknown as void;
    };

    const redirectToUHExperience = () => {
        startNewConversationWithAiMate(searchQuery, { accountId: data.me.user.accountId });
        setIsUHExperienceSelected(true);
    };

    return (
        <>
            {!isUHExperienceSelected && (
                <Stack>
                    <Box xcss={searchBoxStyles} id={'new-search-textbox'}>
                        <Input
                            type="text"
                            autoComplete="off"
                            spellCheck={false}
                            placeholder={formatMessage(messages.inputPlaceholder)}
                            onChange={(e) => openChoicesOnChange(e.target.value)}
                        />

                        <IconButton type="submit" actionSubjectId="search">
                            <SearchIcon primaryColor={token('color.icon', 'gray')} label={'search-button'} />
                        </IconButton>
                    </Box>
                    {isChoiceOpen && (
                        <Box xcss={dropdownStyles}>
                            <Stack css={dropdownStackStyles} grow="fill">
                                {/* eslint-disable-next-line styled-components-a11y/click-events-have-key-events */}
                                <DropDownCard
                                    aria-label="button"
                                    onClick={redirectToUHExperience}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <AtlassianIntelligenceIcon />
                                    <QueryText>{searchQuery}</QueryText>
                                    <Button appearance="subtle-link">
                                        {formatMessage(messages.askAtlassianIntelligence)}
                                    </Button>
                                </DropDownCard>
                                {/* eslint-disable-next-line styled-components-a11y/click-events-have-key-events */}
                                <DropDownCard
                                    aria-label="button"
                                    onClick={redirectToAllResults}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <SearchIcon label="search-icon" size="medium" />
                                    <QueryText>{searchQuery}</QueryText>
                                    <Button appearance="subtle-link">{formatMessage(messages.showAllResults)}</Button>
                                </DropDownCard>
                            </Stack>
                        </Box>
                    )}
                </Stack>
            )}
            {isUHExperienceSelected && (
                <SmartCardProvider>
                    <ConversationExperience />
                </SmartCardProvider>
            )}
        </>
    );
};

const CustomGlyph = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M17.17 12C17.17 11.4477 17.6177 11 18.17 11H20C20.5523 11 21 11.4477 21 12C21 12.5523 20.5523 13 20 13H18.17C17.6177 13 17.17 12.5523 17.17 12Z"
            fill="url(#paint0_linear_6_3430)"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 3C12.5523 3 13 3.44772 13 4V5.83C13 6.38228 12.5523 6.83 12 6.83C11.4477 6.83 11 6.38228 11 5.83V4C11 3.44772 11.4477 3 12 3Z"
            fill="url(#paint1_linear_6_3430)"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M3 12C3 11.4477 3.44772 11 4 11H5.83C6.38228 11 6.83 11.4477 6.83 12C6.83 12.5523 6.38228 13 5.83 13H4C3.44772 13 3 12.5523 3 12Z"
            fill="url(#paint2_linear_6_3430)"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 17.17C12.5523 17.17 13 17.6177 13 18.17V20C13 20.5523 12.5523 21 12 21C11.4477 21 11 20.5523 11 20V18.17C11 17.6177 11.4477 17.17 12 17.17Z"
            fill="url(#paint3_linear_6_3430)"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.2929 14.2929C14.6835 13.9024 15.3166 13.9024 15.7071 14.2929L19.7071 18.2929C20.0977 18.6834 20.0977 19.3166 19.7071 19.7071C19.3166 20.0976 18.6835 20.0976 18.2929 19.7071L14.2929 15.7071C13.9024 15.3166 13.9024 14.6834 14.2929 14.2929Z"
            fill="#2684FF"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M19.7071 4.29289C20.0977 4.68342 20.0977 5.31658 19.7071 5.70711L15.7071 9.70711C15.3166 10.0976 14.6835 10.0976 14.2929 9.70711C13.9024 9.31658 13.9024 8.68342 14.2929 8.29289L18.2929 4.29289C18.6835 3.90237 19.3166 3.90237 19.7071 4.29289Z"
            fill="#2684FF"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.29289 4.29289C4.68342 3.90237 5.31658 3.90237 5.70711 4.29289L9.70711 8.29289C10.0976 8.68342 10.0976 9.31658 9.70711 9.70711C9.31658 10.0976 8.68342 10.0976 8.29289 9.70711L4.29289 5.70711C3.90237 5.31658 3.90237 4.68342 4.29289 4.29289Z"
            fill="#2684FF"
        />
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.70711 14.2929C10.0976 14.6834 10.0976 15.3166 9.70711 15.7071L5.70711 19.7071C5.31658 20.0976 4.68342 20.0976 4.29289 19.7071C3.90237 19.3166 3.90237 18.6834 4.29289 18.2929L8.29289 14.2929C8.68342 13.9024 9.31658 13.9024 9.70711 14.2929Z"
            fill="#2684FF"
        />
        <defs>
            <linearGradient id="paint0_linear_6_3430" x1="21" y1="12" x2="17.5" y2="12" gradientUnits="userSpaceOnUse">
                <stop offset="0.270833" stopColor="#2684FF" />
                <stop offset="1" stopColor="#0052CC" />
            </linearGradient>
            <linearGradient
                id="paint1_linear_6_3430"
                x1="12.5"
                y1="3"
                x2="12.5"
                y2="6.5"
                gradientUnits="userSpaceOnUse"
            >
                <stop offset="0.1875" stopColor="#2684FF" />
                <stop offset="1" stopColor="#0052CC" />
            </linearGradient>
            <linearGradient
                id="paint2_linear_6_3430"
                x1="6.82996"
                y1="12"
                x2="3.32996"
                y2="12"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor="#0052CC" />
                <stop offset="0.791667" stopColor="#2684FF" />
            </linearGradient>
            <linearGradient
                id="paint3_linear_6_3430"
                x1="12.5"
                y1="17"
                x2="12.5"
                y2="21"
                gradientUnits="userSpaceOnUse"
            >
                <stop stopColor="#0052CC" />
                <stop offset="0.802083" stopColor="#2684FF" />
            </linearGradient>
        </defs>
    </svg>
);

const AtlassianIntelligenceIcon = () => {
    return <Icon glyph={CustomGlyph} label="" />;
};

const Input = styled.input`
    ${SEARCH_INPUT_FONT_SIZE};
    ${mixins.placeholder(token('color.text.subtlest', 'gray'))};
    border-radius: 5px;
    padding: ${grid.multiple(2.125).px};
    padding-right: ${token('space.400', '32px')};
    width: 100%;
    border: 0;
    outline: none;
    position: relative;
    z-index: ${searchTextbox};
`;

const IconButton = styled(UnstyledButton)`
    position: absolute;
    padding: 0 ${token('space.200', '16px')};
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    border-radius: 0 ${mixins.borderRadiusValue} ${mixins.borderRadiusValue} 0;
    cursor: pointer;
    z-index: ${searchTextbox};

    &:hover {
        > span {
            ${transitions.zoom()};
        }
    }
`;

const DropDownCard = styled.div`
    display: flex;
    flex-direction: row;
    height: ${token('space.600', '50px')};
    width: '100%';
    justify-content: space-between;
    align-items: center;
    padding: ${token('space.150', '10px')};
    cursor: pointer;

    &:hover {
        background: ${token('color.background.selected.hovered', colors.N0)};
    }
`;

const QueryText = styled.div`
    flex-grow: 1;
    margin-left: ${token('space.150', '10px')};
`;

const searchBoxStyles = xcss({
    position: 'relative',
});

const dropdownStyles = xcss({
    display: 'flex',
    borderRadius: 'border.radius.100',
    background: 'white',
    marginTop: '20px',
});

const dropdownStackStyles = xcss({
    display: 'flex',
    flexDirection: 'column',
});
